import React, { createContext, useContext, useState, useRef, useCallback } from 'react';
import { api } from '../services/api.js';

const DiscoveryContext = createContext();

export const useDiscovery = () => {
  const context = useContext(DiscoveryContext);
  if (!context) {
    throw new Error('useDiscovery must be used within a DiscoveryProvider');
  }
  return context;
};

export const DiscoveryProvider = ({ children }) => {
  // Feed-based state - persists across navigation
  const [patents, setPatents] = useState([]);
  const [feedPatentIds, setFeedPatentIds] = useState([]);
  const [imageMap, setImageMap] = useState({});
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState(null);

  // Search state
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [activeSearchQuery, setActiveSearchQuery] = useState('');
  const [searchError, setSearchError] = useState(null);

  // Daily abandoned-patent view limit, reported by the backend on each feed/search load.
  // Shape: { atLimit, withheld, currentCount, limit } (null for unlimited plans / before load).
  const [viewLimit, setViewLimit] = useState(null);

  // Refs for image fetching
  const fetchedImagesRef = useRef(new Set());
  const imageQueueRef = useRef([]);
  const isProcessingImagesRef = useRef(false);
  const loadingRef = useRef(false);
  const retryCountRef = useRef(0);

  // Optimized batch image fetching
  const fetchPatentImages = useCallback(async (patentsToFetch) => {
    if (isProcessingImagesRef.current) {
      imageQueueRef.current.push(...patentsToFetch);
      return;
    }

    const idsToFetch = patentsToFetch
      .map(p => p.patentId)
      .filter(Boolean)
      .filter(id => !fetchedImagesRef.current.has(id));

    if (idsToFetch.length === 0) return;

    isProcessingImagesRef.current = true;

    // Fetch images in batches of 3 to avoid overwhelming the server
    for (let i = 0; i < idsToFetch.length; i += 3) {
      const batch = idsToFetch.slice(i, i + 3);

      await Promise.all(
        batch.map(async (id) => {
          if (fetchedImagesRef.current.has(id)) return;
          fetchedImagesRef.current.add(id);

          try {
            const json = await api.get(`/patent/${id}`);
            const images = json.success && Array.isArray(json.data?.images) ? json.data.images : [];
            // Always record result (even empty) so UI can show "no image" instead of infinite shimmer
            setImageMap(prev => ({ ...prev, [id]: images }));
          } catch (err) {
            console.error(`Error fetching images for patent ${id}:`, err);
            // Mark as attempted so the shimmer resolves to "no image"
            setImageMap(prev => ({ ...prev, [id]: [] }));
          }
        })
      );

      // Small delay between batches
      if (i + 3 < idsToFetch.length) {
        await new Promise(resolve => setTimeout(resolve, 300));
      }
    }

    isProcessingImagesRef.current = false;

    // Process queued items if any
    if (imageQueueRef.current.length > 0) {
      const queued = [...imageQueueRef.current];
      imageQueueRef.current = [];
      fetchPatentImages(queued);
    }
  }, []);

  // Load initial feed (only if not already loaded)
  const loadInitialFeed = useCallback(async () => {
    if (loadingRef.current || isInitialized) return;

    loadingRef.current = true;
    setError(null);

    try {
      const data = await api.getDiscoveryFeed();

      if (data.success) {
        const newPatents = data.patents || [];
        setPatents(newPatents);
        setFeedPatentIds(data.patentIds || []);
        setViewLimit(data.viewLimit || null);
        // Keep loading already-viewed patents (backend re-serves them even at the limit);
        // only stop once NEW patents are actually withheld.
        setHasMore(data.viewLimit?.withheld > 0 ? false : (data.hasMore ?? false));
        setOffset(newPatents.length);
        setIsInitialized(true);
        retryCountRef.current = 0;

        if (newPatents.length > 0) {
          setTimeout(() => {
            fetchPatentImages(newPatents);
          }, 100);
        }
      } else {
        setError(data.message || 'Failed to load discovery feed');
      }
    } catch (err) {
      console.error('Error loading discovery feed:', err);

      // Auto-retry up to 2 times before showing error
      if (retryCountRef.current < 2) {
        retryCountRef.current += 1;
        console.log(`[Discovery] Retrying feed load (attempt ${retryCountRef.current}/2)...`);
        loadingRef.current = false;
        await new Promise(resolve => setTimeout(resolve, 3000));
        return loadInitialFeed();
      }

      setError('Failed to connect. Please try again later');
      setHasMore(false);
    } finally {
      loadingRef.current = false;
    }
  }, [fetchPatentImages, isInitialized]);

  // Load more patents on scroll
  const loadMorePatents = useCallback(async () => {
    if (loadingRef.current || !hasMore) return false;

    loadingRef.current = true;

    try {
      const data = await api.getMoreDiscoveryPatents(offset);

      if (data.success) {
        const newPatents = data.patents || [];

        if (newPatents.length > 0) {
          setPatents(prev => {
            const existingIds = new Set(prev.map(p => p.patentId));
            const uniqueNewPatents = newPatents.filter(p => !existingIds.has(p.patentId));
            return [...prev, ...uniqueNewPatents];
          });
          setOffset(prev => prev + newPatents.length);

          setTimeout(() => {
            fetchPatentImages(newPatents);
          }, 100);
        }

        setViewLimit(data.viewLimit || null);
        setHasMore(data.viewLimit?.withheld > 0 ? false : (data.hasMore ?? false));
        return true;
      }
    } catch (err) {
      console.error('Error loading more patents:', err);
      setHasMore(false);
    } finally {
      loadingRef.current = false;
    }
    return false;
  }, [offset, hasMore, fetchPatentImages]);

  // Check if currently loading
  const isLoading = () => loadingRef.current;

  // Reset the feed (e.g., for manual refresh)
  const resetFeed = useCallback(() => {
    setPatents([]);
    setFeedPatentIds([]);
    setImageMap({});
    setHasMore(true);
    setOffset(0);
    setIsInitialized(false);
    setError(null);
    setViewLimit(null);
    fetchedImagesRef.current = new Set();
    imageQueueRef.current = [];
    isProcessingImagesRef.current = false;
    loadingRef.current = false;
  }, []);

  // Run a global vector similarity search
  const searchPatents = useCallback(async (query) => {
    const q = query?.trim();
    if (!q) return;

    setIsSearching(true);
    setActiveSearchQuery(q);
    setSearchError(null);
    setSearchResults([]);

    try {
      const data = await api.searchPatents(q);
      if (data.success) {
        const results = data.patents || [];
        setSearchResults(results);
        setViewLimit(data.viewLimit || null);
        if (results.length > 0) {
          setTimeout(() => fetchPatentImages(results), 100);
        }
      } else {
        setSearchError(data.message || 'Search failed');
      }
    } catch (err) {
      console.error('Error searching patents:', err);
      setSearchError('Search failed. Please try again.');
    } finally {
      setIsSearching(false);
    }
  }, [fetchPatentImages]);

  // Clear search and return to feed view
  const clearSearch = useCallback(() => {
    setSearchResults([]);
    setIsSearching(false);
    setActiveSearchQuery('');
    setSearchError(null);
  }, []);

  // ============================================
  // Patent Details Cache - for instant reopening
  // ============================================
  const [patentDetailsCache, setPatentDetailsCache] = useState({});

  // Get cached patent details
  const getCachedPatentDetails = useCallback((patentId) => {
    return patentDetailsCache[patentId] || null;
  }, [patentDetailsCache]);

  // Cache patent details after fetching
  const cachePatentDetails = useCallback((patentId, details) => {
    setPatentDetailsCache(prev => ({
      ...prev,
      [patentId]: {
        ...details,
        cachedAt: Date.now(),
      }
    }));
  }, []);

  // Fetch patent details with caching
  const fetchPatentDetails = useCallback(async (patentId) => {
    // Check cache first
    const cached = patentDetailsCache[patentId];
    if (cached) {
      console.log(`[PatentCache] Returning cached details for ${patentId}`);
      return { success: true, data: cached, source: 'frontend-cache' };
    }

    // Fetch from API
    try {
      const data = await api.get(`/patent/${patentId}`);

      if (data.success && data.data) {
        // Cache the result
        cachePatentDetails(patentId, data.data);
      }

      return data;
    } catch (error) {
      console.error(`Error fetching patent ${patentId}:`, error);
      throw error;
    }
  }, [patentDetailsCache, cachePatentDetails]);

  const value = {
    // State
    patents,
    feedPatentIds,
    imageMap,
    hasMore,
    offset,
    isInitialized,
    error,

    // Search
    searchResults,
    isSearching,
    activeSearchQuery,
    searchError,
    searchPatents,
    clearSearch,

    // Daily view limit
    viewLimit,

    // Feed Actions
    loadInitialFeed,
    loadMorePatents,
    isLoading,
    resetFeed,
    fetchPatentImages,

    // Patent Details Cache
    getCachedPatentDetails,
    cachePatentDetails,
    fetchPatentDetails,
  };

  return (
    <DiscoveryContext.Provider value={value}>
      {children}
    </DiscoveryContext.Provider>
  );
};
